import { Typography, type SxProps } from '@mui/material';
import { Provider, type ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';

import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { hasValidConditionValues } from '@/types/advanceFilter';

import { type BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { isPitrPolicyType } from './backupPoliciesTypes';
import { useBackupPolicySupportedProperties } from './backupPolicyProperties';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';

const useSourceProviders = (
  policyType: BackupPolicyType
): Provider[] | undefined => {
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD) {
    return [Provider.Aws];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY) {
    return [Provider.Aws, Provider.Gcp];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR) {
    return [Provider.Azure];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR) {
    return [Provider.Aws];
  }
  return undefined;
};

export const BackupPolicyConditionEditor = (
  props: StepProps<BackupPolicyEditState>
) => {
  const nextButtonDisabledTooltip =
    props.wizardState.resourcesScopeType === 'useConditions' &&
    !hasValidConditionValues(props.wizardState.condition)
      ? 'Please create a condition with all required fields'
      : 'Please select at least one resource type';

  const supportedResourceTypes =
    useBackupPolicyAvailableTypes()[props.wizardState.policyType];
  const sourceProviders = useSourceProviders(props.wizardState.policyType);
  const supportedProperties = useBackupPolicySupportedProperties(
    props.wizardState.resourceTypes ?? supportedResourceTypes,
    sourceProviders
  );

  return (
    <>
      <StepContainer
        sx={{ padding: 0 }}
        stepperLabels={props.stepperLabels}
        stepperIndex={0}
        nextButtonDisabledTooltip={nextButtonDisabledTooltip}
        canGoNext={() =>
          props.wizardState.resourcesScopeType !== 'useConditions' ||
          hasValidConditionValues(props.wizardState.condition)
        }
        onNextClick={async () => {
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        navigationComponent={
          <RenameEntityName
            focusOnMount={props.wizardState.focusOnName}
            label='Backup policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <BackupPolicySupportedDisclaimer
          policyType={props.wizardState.policyType}
        />
        <ResourcesSelectionEditor
          policyType={props.wizardState.policyType}
          selectedResourceTypes={props.wizardState.resourceTypes}
          supportedProviders={sourceProviders}
          supportedResourceTypes={
            props.wizardState.resourceTypes ?? supportedResourceTypes
          }
          condition={props.wizardState.condition}
          setCondition={(condition) =>
            props.setWizardState({ ...props.wizardState, condition })
          }
          includedResources={props.wizardState.include}
          excludedResources={props.wizardState.exclude}
          setIncludedResources={(includedResources) =>
            props.setWizardState({
              ...props.wizardState,
              include: includedResources,
            })
          }
          setExcludedResources={(excludedResources) =>
            props.setWizardState({
              ...props.wizardState,
              exclude: excludedResources,
            })
          }
          resourcesScopeType={props.wizardState.resourcesScopeType}
          setResourcesScopeType={(resourcesScopeType) =>
            props.setWizardState({
              ...props.wizardState,
              resourcesScopeType,
            })
          }
          supportedProperties={supportedProperties}
          conditionTitle={
            <BackupPolicyConditionTitle
              resourceTypes={props.wizardState.resourceTypes}
              policyType={props.wizardState.policyType}
              sx={{ mb: '24px' }}
            />
          }
          allExcludedDescription={
            'This backup policy won’t be automatically applied to any resources. Only manually included resources will be assigned to the policy.'
          }
          allIncludedDescription={
            props.wizardState.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_PITR ||
            props.wizardState.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR
              ? 'This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they’ll be included too.'
              : 'This backup policy will be automatically applied to all resources. When new ones are discovered, they’ll be included too.'
          }
          manualOverridesDescription='Manually include or exclude resources from your backup policy, regardless of dynamic selection.'
        />
      </StepContainer>
    </>
  );
};

export const BackupPolicyConditionTitle = ({
  resourceTypes,
  policyType,
  sx,
}: {
  resourceTypes?: ResourceType[];
  policyType: BackupPolicyType;
  sx?: SxProps;
}) => {
  const { BackupPolicySingleResourceTypeEnabled } = useFeatureFlags();
  if (BackupPolicySingleResourceTypeEnabled && !isPitrPolicyType(policyType)) {
    return (
      <Typography variant='subtitle1' sx={sx}>
        Include resources based on these conditions:
      </Typography>
    );
  }

  return (
    <Typography variant='subtitle1' sx={sx}>
      Include{' '}
      {resourceTypes ? (
        <span className='font-medium'>
          {resourceTypes?.map((x) => ResourceTypes[x].displayName).join(', ')}
          {' resources '}
        </span>
      ) : (
        'resources '
      )}
      based on these conditions:
    </Typography>
  );
};
